import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Dashboard from '../pages/Dashboard';
import Guardians from '../pages/Guardians';
import Contacts from '../pages/Contacts';
import Accounts from '../pages/Accounts';
import Assets from '../pages/Assets';
import Insurance from '../pages/Insurance';
import Digital from '../pages/Digital';
import Documents from '../pages/Documents';
import Wishes from '../pages/Wishes';

export default function AppShell({ session }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const isDashboard = location.pathname === '/app/dashboard' || location.pathname === '/app/';

  const style = `
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,900;1,900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Plus Jakarta Sans', sans-serif; background: #F8F7FB; }

    .shell { display: flex; flex-direction: column; min-height: 100vh; }

    /* TOP NAV */
    .shell-topnav {
      background: #fff;
      border-bottom: 1px solid #EDE9E3;
      padding: 0 32px;
      height: 58px;
      display: flex; align-items: center; justify-content: space-between;
      position: sticky; top: 0; z-index: 50;
      box-shadow: 0 1px 8px rgba(30,16,64,0.05);
    }
    .shell-logo {
      font-family: 'Fraunces', serif; font-weight: 900; font-size: 22px;
      color: #1E1040; text-decoration: none; cursor: pointer; border: none; background: none;
    }
    .shell-logo span { color: #8B5CF6; font-style: italic; }

    .shell-nav-right {
      display: flex; align-items: center; gap: 8px;
    }
    .shell-guardian-pill {
      display: flex; align-items: center; gap: 6px;
      background: rgba(139,92,246,0.07); border: 1px solid rgba(139,92,246,0.15);
      border-radius: 100px; padding: 6px 14px;
      font-size: 12px; font-weight: 600; color: #7C3AED;
    }
    .shell-user-avatar {
      width: 34px; height: 34px; border-radius: 50%;
      background: linear-gradient(135deg, #8B5CF6, #38BDF8);
      display: flex; align-items: center; justify-content: center;
      font-size: 14px; font-weight: 700; color: #fff;
      cursor: pointer; border: none;
      transition: opacity 0.2s;
      title: attr(data-email);
    }
    .shell-user-avatar:hover { opacity: 0.85; }
    .shell-signout {
      padding: 7px 16px; border: 1px solid #EDE9E3; border-radius: 100px;
      background: transparent; color: #6B7280;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 500;
      cursor: pointer; transition: all 0.15s;
    }
    .shell-signout:hover { background: #FEF2F2; border-color: #FECACA; color: #EF4444; }

    /* back button for inner pages */
    .shell-back {
      display: flex; align-items: center; gap: 6px;
      padding: 6px 14px; border: 1px solid #EDE9E3; border-radius: 100px;
      background: transparent; color: #6B7280;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 500;
      cursor: pointer; transition: all 0.15s;
    }
    .shell-back:hover { background: #F5F3FF; border-color: rgba(139,92,246,0.2); color: #7C3AED; }

    /* SECTION NAV STRIP */
    .shell-section-nav {
      background: #fff;
      border-bottom: 1px solid #EDE9E3;
      padding: 0 24px;
      display: flex; align-items: center; gap: 2px;
      overflow-x: auto;
      position: sticky; top: 58px; z-index: 40;
      box-shadow: 0 2px 8px rgba(30,16,64,0.04);
    }
    .shell-section-nav::-webkit-scrollbar { display: none; }
    .shell-section-nav-item {
      display: flex; align-items: center; gap: 7px;
      padding: 12px 14px; white-space: nowrap;
      font-size: 13px; font-weight: 500; color: #6B7280;
      border: none; background: none; cursor: pointer;
      border-bottom: 2px solid transparent;
      transition: color 0.15s, border-color 0.15s;
      margin-bottom: -1px;
    }
    .shell-section-nav-item:hover { color: #7C3AED; }
    .shell-section-nav-item.active {
      color: #7C3AED; font-weight: 700;
      border-bottom-color: #8B5CF6;
    }
    .shell-section-nav-icon { font-size: 15px; }

    @media(max-width: 640px) {
      .shell-topnav { padding: 0 16px; }
      .shell-guardian-pill { display: none; }
    }
  `;

  const initials = session.user.email?.[0]?.toUpperCase() || '?';

  return (
    <>
      <style>{style}</style>
      <div className="shell">
        {/* TOP NAV */}
        <nav className="shell-topnav">
          <button className="shell-logo" onClick={() => navigate('/app/dashboard')}>
            Merq<span>Legacy</span>
          </button>
          <div className="shell-nav-right">
            {!isDashboard && (
              <button className="shell-back" onClick={() => navigate('/app/dashboard')}>
                ← My Vault
              </button>
            )}
            <div className="shell-guardian-pill">🛡️ Guardian System Active</div>
            <div className="shell-user-avatar" title={session.user.email}>{initials}</div>
            <button className="shell-signout" onClick={handleSignOut}>Sign out</button>
          </div>
        </nav>

        {/* SECTION NAV STRIP — shown on all section pages */}
        {!isDashboard && (
          <nav className="shell-section-nav">
            {[
              { to: '/app/contacts',  icon: '👥', label: 'Key Contacts' },
              { to: '/app/accounts',  icon: '🏦', label: 'Accounts' },
              { to: '/app/assets',    icon: '🏡', label: 'Assets' },
              { to: '/app/insurance', icon: '🛡️', label: 'Insurance' },
              { to: '/app/digital',   icon: '💻', label: 'Digital Life' },
              { to: '/app/documents', icon: '📄', label: 'Documents' },
              { to: '/app/wishes',    icon: '💌', label: 'Final Wishes' },
              { to: '/app/guardians', icon: '🔐', label: 'Vault Guardians' },
            ].map(item => (
              <button
                key={item.to}
                className={`shell-section-nav-item${location.pathname === item.to ? ' active' : ''}`}
                onClick={() => navigate(item.to)}
              >
                <span className="shell-section-nav-icon">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>
        )}

        {/* MAIN CONTENT */}
        <main className="shell-main">
          <Routes>
            <Route path="dashboard" element={<Dashboard session={session} />} />
            <Route path="contacts"  element={<Contacts  session={session} />} />
            <Route path="accounts"  element={<Accounts  session={session} />} />
            <Route path="assets"    element={<Assets    session={session} />} />
            <Route path="insurance" element={<Insurance session={session} />} />
            <Route path="digital"   element={<Digital   session={session} />} />
            <Route path="documents" element={<Documents session={session} />} />
            <Route path="wishes"    element={<Wishes    session={session} />} />
            <Route path="guardians" element={<Guardians session={session} />} />
          </Routes>
        </main>
      </div>
    </>
  );
}

